import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDeviceSessions, type DemoSessionRow } from '../api/hooks';
import { fmtDuration, ulidTimestampMs } from '../components/format';
import { Skeleton } from '../components/Skeleton';

// Decoded session timing. `started` falls back to `ended - active - suspended`
// when the session_id isn't a parseable ULID — should be unusual but the
// fallback keeps us truthful instead of rendering "—".
function deriveStart(row: DemoSessionRow): Date | null {
  const ms = ulidTimestampMs(row.session_id);
  if (ms !== null) return new Date(ms);
  const ended = new Date(row.ts).getTime();
  const active = Number(row.data.active_s ?? 0);
  const susp = Number(row.data.suspended_s ?? 0);
  if (!Number.isFinite(active) || !Number.isFinite(susp)) return null;
  return new Date(ended - (active + susp) * 1000);
}

function fmtTime(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function DeviceSessionsForDay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const day = params.get('day') ?? '';
  const sessions = useDeviceSessions(id, day || undefined);

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/devices/${encodeURIComponent(id ?? '')}`}
          className="text-xs text-accent hover:underline"
        >
          ← back to device
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          Sessions on <span className="tabular-nums">{day || '—'}</span>
        </h1>
        <p className="text-sm text-muted font-mono">{id}</p>
      </div>

      <div className="card overflow-x-auto">
        {sessions.isLoading && (
          <table className="w-full text-sm" aria-label="Loading sessions">
            <tbody>
              <Skeleton.TableRows rows={4} cols={7} />
            </tbody>
          </table>
        )}
        {sessions.error && (
          <p className="text-sm text-warn">{(sessions.error as Error).message}</p>
        )}
        {sessions.data && sessions.data.sessions.length === 0 && (
          <div className="text-center py-10 space-y-1">
            <p className="text-sm text-fg font-medium">No sessions on this day</p>
            <p className="text-xs text-muted">
              Sessions appear once the device finishes a demo run.
            </p>
          </div>
        )}
        {sessions.data && sessions.data.sessions.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted border-b border-border">
                <th className="font-medium py-2 pr-4">Started</th>
                <th className="font-medium py-2 pr-4">Ended</th>
                <th className="font-medium py-2 pr-4">Active</th>
                <th className="font-medium py-2 pr-4">Suspended</th>
                <th className="font-medium py-2 pr-4">Views</th>
                <th className="font-medium py-2 pr-4">Plays</th>
                <th className="font-medium py-2 pr-4">Feedback</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.data.sessions.map((s) => {
                const start = deriveStart(s);
                const end = new Date(s.ts);
                const href = `/devices/${encodeURIComponent(id ?? '')}/sessions/${encodeURIComponent(s.session_id)}`;
                const go = () => navigate(href);
                return (
                  <tr
                    key={s.event_id}
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        go();
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open session starting ${fmtTime(start)}`}
                    className="group/row border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent"
                  >
                    <td className="py-2 pr-4 tabular-nums">{fmtTime(start)}</td>
                    <td className="py-2 pr-4 tabular-nums">{fmtTime(end)}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {fmtDuration(Number(s.data.active_s ?? 0))}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {fmtDuration(Number(s.data.suspended_s ?? 0))}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{s.data.view_count ?? 0}</td>
                    <td className="py-2 pr-4 tabular-nums">{s.data.content_play_count ?? 0}</td>
                    <td className="py-2 pr-4">{s.data.reached_feedback ? '✓' : '—'}</td>
                    <td
                      className="py-2 pr-1 text-right text-muted opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
                      aria-hidden="true"
                    >
                      ›
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
